/**
 * 路由守卫 —— 登录态 + 权限校验。
 *
 * 规则：
 *   1. 未登录访问受保护路由 → 跳 A01 登录页，并带上 redirect 以便登录后回跳；
 *   2. 已登录访问登录页 → 直接进工作台；
 *   3. 无权限 → 不允许进入目标路由（跳 403 提示页，而不是静默放行）；
 *   4. 已登录但没有 profile（刷新页面后 token 还在）→ 先拉一次 /admin/auth/profile；
 *      **拉不到（接口不可用）时不把用户踢出去**，而是放行到降级态，
 *      由页面自己展示「接口不可用」提示。这是任务书「接口不可用必须降级」的要求。
 */
import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router'
import { ElMessage } from 'element-plus'
import { hasPermission, requiredPermissions } from '@/utils/permission'
import { LOGIN_PATH } from '@/api/request'
import type { useAuthStore } from '@/store/auth'

export const LOGIN_ROUTE_NAME = 'AdminLogin'
export const FORBIDDEN_ROUTE_NAME = 'AdminForbidden'

export interface GuardDeps {
  auth: ReturnType<typeof useAuthStore>
  /** 通知（便于测试替换） */
  warn?: (msg: string) => void
}

/**
 * 纯函数形式的判定，便于单测直接覆盖而不必启动整个 router。
 * 返回 'allow' | 'login' | 'forbidden' | 'login-page'
 */
export function resolveNavigation(
  to: Pick<RouteLocationNormalized, 'meta' | 'name' | 'fullPath' | 'matched'>,
  opts: { loggedIn: boolean; permissions: readonly string[]; profileLoaded: boolean },
): 'allow' | 'login' | 'forbidden' | 'login-page' {
  const isLoginRoute = to.name === LOGIN_ROUTE_NAME || to.meta.publicRoute === true

  if (isLoginRoute) {
    if (opts.loggedIn && to.name === LOGIN_ROUTE_NAME) return 'login-page'
    return 'allow'
  }

  if (!opts.loggedIn) return 'login'

  // 权限校验：取本路由链上最严格的要求
  const required = new Set<string>()
  for (const record of to.matched ?? []) {
    for (const code of requiredPermissions(record.meta as Record<string, unknown>)) required.add(code)
  }
  for (const code of requiredPermissions(to.meta as Record<string, unknown>)) required.add(code)
  if (required.size > 0) {
    const list = [...required]
    if (!hasPermission(opts.permissions, list)) return 'forbidden'
  }

  return 'allow'
}

export function createAuthGuard(deps: GuardDeps): NavigationGuardWithThis<undefined> {
  const warn = deps.warn ?? ((msg: string) => ElMessage.warning(msg))

  return async (to) => {
    const auth = deps.auth

    // 刷新页面后 token 仍在但内存 profile 丢了：先补一次
    if (auth.loggedIn && !auth.profileLoaded) {
      await auth.ensureProfile()
    }

    const decision = resolveNavigation(to, {
      loggedIn: auth.loggedIn,
      permissions: auth.permissions,
      profileLoaded: auth.profileLoaded,
    })

    if (decision === 'login-page') {
      return { name: 'Dashboard' }
    }

    if (decision === 'login') {
      return { path: LOGIN_PATH, query: { redirect: to.fullPath } }
    }

    if (decision === 'forbidden') {
      warn('没有权限访问该页面（前端仅做体验层拦截，后端会二次校验）')
      return { name: FORBIDDEN_ROUTE_NAME }
    }

    // 接口不可用（后端未启动）时给出显式提示，但继续放行，让页面降级展示
    if (auth.profileError && auth.profileError.network) {
      warn('接口不可用，页面已切换为降级展示')
    }

    return true
  }
}

/** 注册到 router；main.ts 调用 */
export function installGuards(
  router: { beforeEach: (fn: NavigationGuardWithThis<undefined>) => void; beforeResolve?: unknown },
  deps: GuardDeps,
): void {
  router.beforeEach(createAuthGuard(deps))
}
